/**
 * First Sale Conversion Tracker Page
 *
 * A guided milestone page helping new insurance agents close their first sale.
 * Renders 8 sequential steps with progress tracking, notes, and a celebration
 * state when all steps are complete.
 *
 * Phase 1: Quest-style step indicator.
 * Phase 2: Gamification side quests with auto-detection and points.
 */

import React from 'react'
import { autorun } from 'mobx'
import { Prompt } from 'react-router-dom'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import { MDBContainer, MDBBtn, MDBIcon, MDBCard, MDBCardBody } from 'mdb-react-ui-kit'
import LoadingSpinner from '../../components/shared/LoadingSpinner.component'
import PageHeading from '../../components/shared/PageHeading.component'
import ProgressHeader from './components/ProgressHeader/ProgressHeader.component'
import StepCard from './components/StepCard/StepCard.component'
import CelebrationSection from './components/CelebrationSection/CelebrationSection.component'
import SideQuestsList from './components/SideQuestsList/SideQuestsList.component'
import ResetConfirmModal from './components/ResetConfirmModal/ResetConfirmModal.component'
import AgencyContentStore from '../../shared/stores/agencyContent.store'
import Store from './FirstSaleTracker.store'
import { STEP_CONFIG, STEP_CONTENT_SLUGS } from './FirstSaleTracker.constants'

import './FirstSaleTracker.page.scss'

class FirstSaleTrackerPage extends React.Component {
  stepRefs = {}
  hasScrolled = false
  scrollTimer = null
  _dispose = null

  componentDidMount() {
    Store.fetchTracker()
    AgencyContentStore.fetchByContentSlugs({ contentSlugs: STEP_CONTENT_SLUGS })
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    // Bridge MobX observables to React re-renders.
    // autorun tracks accessed observables and re-runs when they change.
    let isFirstRun = true
    this._dispose = autorun(() => {
      void Store.loading
      void Store.error
      void Store.steps.length
      void Store.completedCount
      void Store.saving
      void Store.hasDirtyNotes
      void Store.welcomeDismissed
      void Store.activeStepNumber
      void Store.isComplete

      if (isFirstRun) {
        isFirstRun = false
        return
      }
      this.forceUpdate()
    })
  }

  componentDidUpdate() {
    // Auto-scroll to active step on mobile after data loads
    if (
      !Store.loading &&
      Store.steps.length > 0 &&
      Store.completedCount > 0 &&
      Store.activeStepNumber &&
      !this.hasScrolled &&
      window.innerWidth < 768
    ) {
      this.hasScrolled = true
      this.scrollTimer = setTimeout(() => {
        const ref = this.stepRefs[Store.activeStepNumber]
        if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    clearTimeout(this.scrollTimer)
    if (this._dispose) this._dispose()
  }

  handleBeforeUnload = e => {
    if (Store.hasDirtyNotes) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  scrollToNextStep = nextStepNumber => {
    if (window.innerWidth >= 768) return
    clearTimeout(this.scrollTimer)
    this.scrollTimer = setTimeout(() => {
      const ref = this.stepRefs[nextStepNumber]
      if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 500)
  }

  setStepRef = (stepNumber, el) => {
    this.stepRefs[stepNumber] = el
  }

  renderLoading() {
    return <LoadingSpinner isActive={true} page='First Sale Tracker' size='sm' />
  }

  renderError() {
    return (
      <MDBCard className='error-state'>
        <MDBCardBody className='text-center py-5'>
          <MDBIcon icon='exclamation-triangle' size='3x' className='text-warning mb-3' />
          <h5>We couldn't load your progress</h5>
          <p className='text-muted'>Your completed steps are safe. This is a temporary issue.</p>
          <MDBBtn type='button' color='primary' size='sm' onClick={() => Store.fetchTracker()}>
            Try Again
          </MDBBtn>
        </MDBCardBody>
      </MDBCard>
    )
  }

  renderWelcomeBanner() {
    if (Store.welcomeDismissed || Store.completedCount > 0) return null
    return (
      <MDBCard className='welcome-banner mb-3'>
        <MDBCardBody className='d-flex align-items-center justify-content-between py-3 px-4'>
          <div>
            <strong>Welcome to your First Sale Tracker!</strong>
            <p className='mb-0 mt-1 text-muted'>
              Complete these 8 milestones to close your first sale. Each step builds on the last — work through them in
              order.
            </p>
          </div>
          <MDBBtn type='button' color='flat' size='sm' className='ml-3 p-2' onClick={() => Store.dismissWelcome()}>
            <MDBIcon icon='times' />
          </MDBBtn>
        </MDBCardBody>
      </MDBCard>
    )
  }

  renderStepList() {
    return Store.steps.map(step => {
      const stepNumber = parseInt(step.step_number)
      const config = STEP_CONFIG[step.step_key] || {}
      const isCompleted = parseInt(step.completed) === 1
      const prevStep = Store.steps.find(s => parseInt(s.step_number) === stepNumber - 1)
      const isLocked = stepNumber > 1 && (!prevStep || parseInt(prevStep.completed) !== 1)
      const isActive = !isCompleted && !isLocked

      return (
        <StepCard
          key={step.id || stepNumber}
          step={step}
          config={config}
          stepNumber={stepNumber}
          isLocked={isLocked}
          isActive={isActive}
          isCompleted={isCompleted}
          onScrollToNext={this.scrollToNextStep}
          ref={el => this.setStepRef(stepNumber, el)}
        />
      )
    })
  }

  render() {
    const { match, location } = this.props

    const resetButton = (
      <MDBBtn
        type='button'
        outline
        color='danger'
        size='sm'
        onClick={() => Store.openResetConfirm()}
        disabled={Store.saving || Store.completedCount === 0}
      >
        <MDBIcon icon='redo-alt' className='mr-1' />
        Reset
      </MDBBtn>
    )

    return (
      <DashboardLayout>
        <main id='FirstSaleTrackerPage' className='mainSection'>
          <Prompt when={Store.hasDirtyNotes} message='You have unsaved notes. Leave without saving?' />

          <MDBContainer fluid className='mt-5'>
            <PageHeading
              label='First Sale Conversion Tracker'
              url={match?.url}
              from={location?.state?.from}
              btnGroup={resetButton}
            />
          </MDBContainer>

          <MDBContainer fluid className='mt-2 pb-5'>
            {!Store.loading && !Store.error ? this.renderWelcomeBanner() : <></>}

            <ProgressHeader />

            {Store.loading && this.renderLoading()}
            {!Store.loading && Store.error && this.renderError()}
            {!Store.loading && !Store.error && (
              <>
                <div className='tracker-layout'>
                  <div className='step-list'>{this.renderStepList()}</div>
                  <SideQuestsList />
                </div>
                <CelebrationSection />
              </>
            )}
          </MDBContainer>

          <ResetConfirmModal />
        </main>
      </DashboardLayout>
    )
  }
}

export default FirstSaleTrackerPage
